import { 
  collection, 
  getDocs, 
  doc, 
  updateDoc, 
  deleteDoc,
  query,
  orderBy,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { User } from '../types';

class UserService {
  private collectionName = 'users';

  // 모든 사용자 조회
  async getAllUsers(): Promise<User[]> {
    try {
      const q = query(
        collection(db, this.collectionName),
        orderBy('createdAt', 'desc')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          uid: data.uid || doc.id,
          email: data.email || '',
          name: data.name || data.displayName || '',
          displayName: data.displayName,
          role: data.role || 'user',
          department: data.department,
          customerId: data.customerId,
          customerCode: data.customerCode,
          companyName: data.companyName,
          createdAt: data.createdAt instanceof Timestamp 
            ? data.createdAt.toDate() 
            : new Date(data.createdAt)
        } as User;
      });
    } catch (error) {
      console.error('Error getting users:', error);
      throw error;
    }
  }

  // 사용자 권한 변경
  async updateUserRole(userId: string, newRole: 'user' | 'admin'): Promise<void> {
    try {
      const userRef = doc(db, this.collectionName, userId);
      await updateDoc(userRef, {
        role: newRole
      });
    } catch (error) {
      console.error('Error updating user role:', error);
      throw error;
    }
  }

  // 사용자 정보 업데이트 (v2.0 확장)
  async updateUserInfo(userId: string, updates: {
    role?: string;
    customerId?: string;
    customerCode?: string;
    companyName?: string;
    department?: string;
  }): Promise<void> {
    try {
      console.log('🔧 userService.updateUserInfo 호출:', { userId, updates });
      
      // undefined 값 제거 (Firestore는 undefined 허용 안 함)
      const cleanUpdates: any = {};
      Object.keys(updates).forEach(key => {
        const value = (updates as any)[key];
        if (value !== undefined && value !== null) {
          cleanUpdates[key] = value;
        }
      });
      
      console.log('🧹 정제된 updates:', cleanUpdates);
      
      const userRef = doc(db, this.collectionName, userId);
      await updateDoc(userRef, cleanUpdates);
      console.log('✅ Firestore 업데이트 완료');
    } catch (error) {
      console.error('❌ userService.updateUserInfo 에러:', error);
      throw error;
    }
  }

  // 사용자 삭제
  async deleteUser(userId: string): Promise<void> {
    try {
      const userRef = doc(db, this.collectionName, userId);
      await deleteDoc(userRef);
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  // 사용자 통계
  async getUserStats() {
    try {
      const users = await this.getAllUsers();
      
      return {
        total: users.length,
        admins: users.filter(u => u.role === 'admin').length,
        regularUsers: users.filter(u => u.role === 'user').length
      };
    } catch (error) {
      console.error('Error getting user stats:', error);
      throw error;
    }
  }
}

export const userService = new UserService();
